import { useEffect, useMemo, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet.markercluster/dist/MarkerCluster.css";
import "leaflet.markercluster/dist/MarkerCluster.Default.css";
import "leaflet.markercluster";
import { Maximize2, Minimize2 } from "lucide-react";
import { MAP } from "@/constants/testIds";
import { donationUrlFor } from "@/lib/links";
import {
  WARD_ELA_2025,
  WARD_ELA_CITYWIDE_2025,
  WARD_ELA_SOURCE,
  WARD_ELA_SR_DESCRIPTION,
  colorForProficiency,
} from "@/lib/wardEla";
import {
  MD_COUNTY_ELA_2025,
  MD_STATEWIDE_AVG_PCT,
  MD_ELA_SOURCE,
  MD_ELA_SR_DESCRIPTION,
  colorForMdProficiency,
} from "@/lib/marylandEla";
import {
  VA_SOL_READING_2025,
  VA_STATEWIDE_PCT,
  VA_SOL_SOURCE,
  VA_SOL_SR_DESCRIPTION,
  VA_SOL_RIGOR_NOTE,
  VA_BOUNDS,
  VA_NEW_RIGOR_STANDARD,
  VA_RIGOR_BADGE_LABEL,
  VA_RIGOR_CHANGE_NOTE,
  VA_DATA_SCHOOL_YEAR,
  colorForVaSol,
} from "@/lib/virginiaSol";

const ICONS = {
  pending: `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='white' stroke-width='2.4' stroke-linecap='round' stroke-linejoin='round'><circle cx='12' cy='12' r='10'/><path d='M12 8v4l3 2'/></svg>`,
  scheduled: `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='white' stroke-width='2.4' stroke-linecap='round' stroke-linejoin='round'><rect x='3' y='4' width='18' height='18' rx='2' ry='2'/><line x1='16' y1='2' x2='16' y2='6'/><line x1='8' y1='2' x2='8' y2='6'/><line x1='3' y1='10' x2='21' y2='10'/></svg>`,
  active: `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='white' stroke-width='2.2' stroke-linecap='round' stroke-linejoin='round'><path d='M2 4h6a4 4 0 0 1 4 4v12a3 3 0 0 0-3-3H2z'/><path d='M22 4h-6a4 4 0 0 0-4 4v12a3 3 0 0 1 3-3h7z'/></svg>`,
};

function makeIcon(status, isNew = false) {
  const newCls = isNew ? " bsp-marker--new" : "";
  return L.divIcon({
    className: "",
    html: `<div class="bsp-marker bsp-marker--${status}${newCls}">${ICONS[status] || ICONS.pending}</div>`,
    iconSize: [30, 30],
    iconAnchor: [15, 15],
    popupAnchor: [0, -16],
  });
}

function popupHtml(f) {
  const date = (s) => (s ? new Date(s).toLocaleDateString() : "—");
  // Avoid "Ward 8 · Ward 8 · DC" — when the portal sets neighborhood = "Ward N"
  // (because no real neighborhood was given), skip the duplicate.
  const wardLabel = f.ward ? `Ward ${f.ward}` : "";
  const nb = (f.neighborhood || "").trim();
  const showNb = nb && nb.toLowerCase() !== wardLabel.toLowerCase();
  const subParts = [showNb ? nb : "", wardLabel, f.jurisdiction].filter(Boolean);
  const head = `<div style="font-family:Lora,serif;font-size:16px;font-weight:600;color:#1C1917;margin-bottom:4px;">${f.first_name} ${f.last_initial}.</div>`;
  const sub = `<div style="font-size:12px;color:#57534E;margin-bottom:8px;">${subParts.join(" · ")}</div>`;
  if (f.status === "active") {
    const donateUrl = donationUrlFor("pin-popup");
    return (
      head + sub +
      `<div style="font-size:13px;color:#1C1917;"><strong>Installed</strong> · ${date(f.delivery_date)}</div>` +
      (f.family_quote ? `<div style="margin-top:6px;font-style:italic;color:#44403C;font-size:13px;">"${f.family_quote}"</div>` : "") +
      `<div style="margin-top:8px;font-size:11px;color:#22C55E;font-weight:600;letter-spacing:0.1em;text-transform:uppercase;">${f.num_children} ${f.num_children === 1 ? "child" : "children"} reached</div>` +
      `<a href="${donateUrl}" target="_blank" rel="noreferrer" style="display:block;margin-top:10px;width:100%;box-sizing:border-box;background:#3DA9DC;color:#fff;border-radius:9999px;padding:8px 12px;font-family:Figtree,sans-serif;font-weight:600;font-size:12px;text-decoration:none;text-align:center;letter-spacing:0.02em;">Sponsor a Library →</a>` +
      `<button class="bsp-share-btn" data-family-id="${f.id}" style="margin-top:6px;width:100%;background:#122036;color:#fff;border:0;border-radius:9999px;padding:8px 12px;font-family:Figtree,sans-serif;font-weight:600;font-size:12px;cursor:pointer;letter-spacing:0.04em;">Share this story →</button>`
    );
  }
  if (f.status === "scheduled") {
    return (
      head + sub +
      `<div style="font-size:13px;color:#1C1917;"><strong>Scheduled</strong> · ${date(f.scheduled_date)}</div>` +
      `<div style="margin-top:4px;font-size:12px;color:#57534E;">Registered ${date(f.registration_date)}</div>`
    );
  }
  return (
    head + sub +
    `<div style="font-size:13px;color:#1C1917;"><strong>Awaiting library</strong></div>` +
    `<div style="margin-top:4px;font-size:12px;color:#57534E;">Registered ${date(f.registration_date)} · ${f.num_children} ${f.num_children === 1 ? "child" : "children"}</div>`
  );
}

export default function ImpactMap({
  families,
  layers,
  newlyInstalledIds = new Set(),
  onShare,
  showLiteracyLayer = true,
  showMdLiteracyLayer = false,
  showVaLiteracyLayer = false,
  setShowVaLiteracyLayer,
  showDcplLayer = false,
}) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const clusterRef = useRef(null);
  const individualRef = useRef(null);
  const choroplethRef = useRef(null);
  const mdChoroplethRef = useRef(null);
  const vaChoroplethRef = useRef(null);
  const dcplLayerRef = useRef(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [insightDismissed, setInsightDismissed] = useState(false);
  const [showVaRigor, setShowVaRigor] = useState(false);

  // Per-ward pin counts (recomputed when families change)
  const wardPinCounts = useMemo(() => {
    const out = {};
    for (const f of families) {
      if (f.jurisdiction !== "DC" || !f.ward) continue;
      const k = String(f.ward).trim();
      if (!out[k]) out[k] = { installed: 0, pending: 0, scheduled: 0 };
      if (f.status === "active") out[k].installed += 1;
      else if (f.status === "pending") out[k].pending += 1;
      else if (f.status === "scheduled") out[k].scheduled += 1;
    }
    return out;
  }, [families]);
  const wardPinCountsRef = useRef(wardPinCounts);
  useEffect(() => { wardPinCountsRef.current = wardPinCounts; }, [wardPinCounts]);

  // Per-MD-county installed-library counts (matches by case-insensitive
  // neighborhood/jurisdiction name → MD_COUNTY_ELA_2025 entry).
  const mdCountyInstalledCounts = useMemo(() => {
    const out = {};
    const names = Object.entries(MD_COUNTY_ELA_2025).map(([fips, v]) => ({
      fips,
      key: v.name.toLowerCase(),
    }));
    for (const f of families) {
      if (f.jurisdiction !== "MD") continue;
      const nb = String(f.neighborhood || "").toLowerCase().replace(/\s+county$/i, "").trim();
      if (!nb) continue;
      const match = names.find((n) => nb === n.key || nb.startsWith(n.key));
      if (!match) continue;
      const bucket = out[match.fips] || { installed: 0, pending: 0 };
      if (f.status === "active") bucket.installed += 1;
      else if (f.status === "pending") bucket.pending += 1;
      out[match.fips] = bucket;
    }
    return out;
  }, [families]);
  const mdCountyCountsRef = useRef(mdCountyInstalledCounts);
  useEffect(() => { mdCountyCountsRef.current = mdCountyInstalledCounts; }, [mdCountyInstalledCounts]);

  // Per-VA-division installed-library counts. Matches families' `neighborhood`
  // field (case-insensitive, trims "County"/"City" suffixes) against the
  // VA_SOL_READING_2025 division names.
  const vaDivisionCounts = useMemo(() => {
    const out = {};
    const entries = Object.entries(VA_SOL_READING_2025).map(([fips, v]) => ({
      fips,
      key: v.division.toLowerCase().replace(/\s+(county|city)$/i, "").trim(),
    }));
    for (const f of families) {
      if (f.jurisdiction !== "VA") continue;
      const nb = String(f.neighborhood || "").toLowerCase().replace(/\s+(county|city)$/i, "").trim();
      if (!nb) continue;
      const match = entries.find((e) => nb === e.key || nb.startsWith(e.key));
      if (!match) continue;
      const bucket = out[match.fips] || { installed: 0, pending: 0 };
      if (f.status === "active") bucket.installed += 1;
      else if (f.status === "pending") bucket.pending += 1;
      out[match.fips] = bucket;
    }
    return out;
  }, [families]);
  const vaDivisionCountsRef = useRef(vaDivisionCounts);
  useEffect(() => { vaDivisionCountsRef.current = vaDivisionCounts; }, [vaDivisionCounts]);

  // Initialize map once
  useEffect(() => {
    if (mapRef.current || !containerRef.current) return;

    const map = L.map(containerRef.current, {
      center: [38.9072, -77.0369], // DC
      zoom: 10,
      minZoom: 7,
      maxZoom: 17,
      zoomControl: true,
      scrollWheelZoom: true,
      attributionControl: true,
    });

    // Base tiles — OpenStreetMap standard. Chosen because:
    //  (a) free with no API key (CARTO retired its no-key free tier),
    //  (b) labels are rendered at proper pixel-font sizes so they stay
    //      crisp on retina/HiDPI displays (Esri raster tiles blurred),
    //  (c) a CSS grayscale/brightness filter (`bsp-base-tiles` below in
    //      index.css) desaturates them to match the previous light-canvas
    //      aesthetic without competing with the ELA choropleth.
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution:
        '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      subdomains: ["a", "b", "c"],
      maxZoom: 19,
      className: "bsp-base-tiles",
    }).addTo(map);

    // Choropleth layer (DC wards shaded by 2025 ELA proficiency).
    // Loaded once from the static asset shipped with the frontend.
    const choropleth = L.geoJSON(null, {
      style: (feature) => {
        const w = feature?.properties?.ward;
        const ela = WARD_ELA_2025[w];
        return {
          fillColor: colorForProficiency(ela?.proficiency_pct),
          fillOpacity: 0.62,
          weight: 1.5,
          color: "#FFFFFF",
          opacity: 0.9,
        };
      },
      onEachFeature: (feature, layer) => {
        const w = feature?.properties?.ward;
        const ela = WARD_ELA_2025[w] || {};
        layer.on("mouseover", (e) => {
          e.target.setStyle({ weight: 2.5, color: "#1C1917", opacity: 1, fillOpacity: 0.72 });
          if (e.target.bringToFront) e.target.bringToFront();
        });
        layer.on("mouseout", () => {
          choropleth.resetStyle(layer);
        });
        layer.bindTooltip(
          () => {
            const counts = wardPinCountsRef.current[String(w)] || { installed: 0, pending: 0 };
            const trendLine = ela.improved
              ? `↑ Improved in 2025 <span style="color:#78716C;font-weight:400;">(citywide +${WARD_ELA_CITYWIDE_2025.delta_pts} pts)</span>`
              : "—";
            return `
              <div style="font-family:Figtree,sans-serif;min-width:230px;">
                <div style="font-family:Lora,serif;font-size:15px;font-weight:700;color:#1C1917;margin-bottom:6px;">Ward ${w}</div>
                <div style="font-size:12px;color:#1C1917;margin-bottom:2px;">
                  <strong>ELA Proficiency (2025):</strong> ${ela.proficiency_pct ?? "—"}%
                </div>
                <div style="font-size:12px;color:#16A34A;margin-bottom:6px;">
                  <strong>Trend:</strong> ${trendLine}
                </div>
                <div style="font-size:12px;color:#1C1917;">
                  <span style="display:inline-block;width:8px;height:8px;border-radius:99px;background:#22C55E;margin-right:6px;"></span>
                  Libraries Installed: <strong>${counts.installed}</strong>
                </div>
                <div style="font-size:12px;color:#1C1917;margin-top:2px;">
                  <span style="display:inline-block;width:8px;height:8px;border-radius:99px;background:#F59E0B;margin-right:6px;"></span>
                  Libraries Pending: <strong>${counts.pending}</strong>
                </div>
              </div>
            `;
          },
          { sticky: true, direction: "top", className: "bsp-ward-tooltip" }
        );
      },
    });
    choropleth.addTo(map);
    choroplethRef.current = choropleth;

    // Lazy-fetch the ward boundaries so they don't block first paint.
    fetch("/dc-wards.geojson")
      .then((r) => (r.ok ? r.json() : null))
      .then((geo) => {
        if (geo && choroplethRef.current) {
          choroplethRef.current.addData(geo);
          // Make sure pins still render above the polygons.
          if (clusterRef.current && clusterRef.current.bringToFront) clusterRef.current.bringToFront();
          if (individualRef.current && individualRef.current.bringToFront) individualRef.current.bringToFront();
        }
      })
      .catch(() => { /* fail silently — pins still work */ });

    // Maryland county choropleth (MCAP 2024-25). Built but NOT auto-added to
    // the map — parent controls visibility via the showMdLiteracyLayer prop.
    const mdChoropleth = L.geoJSON(null, {
      style: (feature) => {
        const fips = feature?.properties?.geoid;
        const row = MD_COUNTY_ELA_2025[fips];
        return {
          fillColor: colorForMdProficiency(row?.ela_pct),
          fillOpacity: 0.75,
          weight: 1,
          color: "#9E2020",
          opacity: 0.85,
        };
      },
      onEachFeature: (feature, layer) => {
        const fips = feature?.properties?.geoid;
        const row = MD_COUNTY_ELA_2025[fips] || {};
        const displayName = row.is_city ? row.name : `${row.name || feature?.properties?.name} County`;
        layer.options.alt = row.ela_pct != null
          ? `${displayName}, Maryland — ${row.ela_pct.toFixed(1)}% ELA proficiency`
          : displayName;
        layer.on("mouseover", (e) => {
          e.target.setStyle({ weight: 2, color: "#7A1818", opacity: 1, fillOpacity: 0.95 });
          if (e.target.bringToFront) e.target.bringToFront();
        });
        layer.on("mouseout", () => {
          mdChoropleth.resetStyle(layer);
        });
        layer.bindTooltip(
          () => {
            const counts = mdCountyCountsRef.current[fips] || { installed: 0 };
            const pctStr = row.ela_pct != null ? `${row.ela_pct.toFixed(1)}%` : "—";
            const installedLine = counts.installed > 0
              ? `<div style="font-size:12px;color:#16A34A;margin-top:6px;">
                   <span style="display:inline-block;width:8px;height:8px;border-radius:99px;background:#22C55E;margin-right:6px;"></span>
                   ${counts.installed} in-home ${counts.installed === 1 ? "library" : "libraries"} installed here
                 </div>`
              : "";
            return `
              <div style="font-family:Figtree,sans-serif;min-width:220px;">
                <div style="font-family:Lora,serif;font-size:15px;font-weight:700;color:#1C1917;margin-bottom:6px;">${displayName}</div>
                <div style="font-size:12px;color:#1C1917;margin-bottom:2px;">
                  <strong>ELA Proficiency:</strong> ${pctStr}
                </div>
                <div style="font-size:11px;color:#78716C;">Source: ${MD_ELA_SOURCE}</div>
                ${installedLine}
              </div>
            `;
          },
          { sticky: true, direction: "top", className: "bsp-ward-tooltip" }
        );
      },
    });
    mdChoroplethRef.current = mdChoropleth;

    fetch("/maryland-counties.geojson")
      .then((r) => (r.ok ? r.json() : null))
      .then((geo) => {
        if (geo && mdChoroplethRef.current) {
          mdChoroplethRef.current.addData(geo);
        }
      })
      .catch(() => { /* fail silently */ });

    // Virginia school-division choropleth (SOL Reading 2024-25). Lazy-fetched,
    // controlled via showVaLiteracyLayer prop. Divisions without published data
    // render in light gray (#E8E8E8) with a "data not available" tooltip.
    const vaChoropleth = L.geoJSON(null, {
      style: (feature) => {
        const fips = feature?.properties?.geoid;
        const row = VA_SOL_READING_2025[fips];
        return {
          fillColor: colorForVaSol(row?.read_pct),
          fillOpacity: row ? 0.75 : 0.5,
          weight: 1,
          color: row ? "#9E2020" : "#A8A29E",
          opacity: row ? 0.85 : 0.6,
        };
      },
      onEachFeature: (feature, layer) => {
        const fips = feature?.properties?.geoid;
        const row = VA_SOL_READING_2025[fips];
        const featureName = feature?.properties?.name || "";
        const isCity = feature?.properties?.is_city;
        const fallbackLabel = isCity ? `${featureName} City` : `${featureName} County`;
        const displayName = row ? row.division : fallbackLabel;
        layer.options.alt = row
          ? `${displayName}: SOL Reading pass rate ${row.read_pct}%`
          : `${displayName}: data not available`;
        layer.on("mouseover", (e) => {
          e.target.setStyle({
            weight: 2,
            color: row ? "#7A1818" : "#57534E",
            opacity: 1,
            fillOpacity: row ? 0.95 : 0.7,
          });
          if (e.target.bringToFront) e.target.bringToFront();
        });
        layer.on("mouseout", () => {
          vaChoropleth.resetStyle(layer);
        });
        layer.bindTooltip(
          () => {
            if (!row) {
              return `
                <div style="font-family:Figtree,sans-serif;min-width:200px;">
                  <div style="font-family:Lora,serif;font-size:14px;font-weight:700;color:#1C1917;margin-bottom:4px;">${displayName}</div>
                  <div style="font-size:12px;color:#78716C;">Data not available</div>
                  <div style="font-size:11px;color:#78716C;margin-top:4px;">Visit schoolquality.virginia.gov</div>
                </div>
              `;
            }
            const counts = vaDivisionCountsRef.current[fips] || { installed: 0 };
            const installedLine = counts.installed > 0
              ? `<div style="font-size:12px;color:#16A34A;margin-top:6px;">
                   <span style="display:inline-block;width:8px;height:8px;border-radius:99px;background:#22C55E;margin-right:6px;"></span>
                   ${counts.installed} in-home ${counts.installed === 1 ? "library" : "libraries"} installed here
                 </div>`
              : "";
            const largestLine = row.largest
              ? `<div style="font-size:11px;color:#78716C;margin-top:2px;font-style:italic;">Largest school division in Virginia</div>`
              : "";
            const rigorLine = VA_NEW_RIGOR_STANDARD
              ? `<div style="margin-top:8px;padding:6px 8px;border-radius:6px;background:#FEF3C7;border:1px solid #FDE68A;font-size:11px;color:#92400E;line-height:1.35;">
                   <strong>${VA_RIGOR_BADGE_LABEL}.</strong> Not directly comparable to prior years — VDOE raised cut scores in Sept 2025.
                 </div>`
              : "";
            return `
              <div style="font-family:Figtree,sans-serif;min-width:230px;">
                <div style="font-family:Lora,serif;font-size:15px;font-weight:700;color:#1C1917;margin-bottom:6px;">${row.division}</div>
                <div style="font-size:12px;color:#1C1917;margin-bottom:2px;">
                  <strong>SOL Reading:</strong> ${row.read_pct}%
                </div>
                <div style="font-size:11px;color:#78716C;">Source: ${VA_SOL_SOURCE}</div>
                ${largestLine}
                ${installedLine}
                ${rigorLine}
              </div>
            `;
          },
          { sticky: true, direction: "top", className: "bsp-ward-tooltip" }
        );
      },
    });
    vaChoroplethRef.current = vaChoropleth;

    fetch("/virginia-divisions.geojson")
      .then((r) => (r.ok ? r.json() : null))
      .then((geo) => {
        if (geo && vaChoroplethRef.current) {
          vaChoroplethRef.current.addData(geo);
        }
      })
      .catch(() => { /* fail silently */ });

    // DC Public Library branches — neutral soft-gold pins, intentionally
    // smaller than family-installation markers so they read as ambient
    // ecosystem context rather than competing for attention with the
    // choropleth or installed pins. Built but NOT auto-added to the map
    // (parent controls visibility via showDcplLayer prop).
    const dcplIcon = L.divIcon({
      className: "bsp-dcpl-marker",
      iconSize: [22, 22],
      iconAnchor: [11, 11],
      html: `<div style="width:22px;height:22px;border-radius:99px;background:#B89968;
        border:2px solid #FFFFFF;box-shadow:0 1px 4px rgba(28,25,23,0.32);
        display:grid;place-items:center;color:#FFFFFF;">
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor"
          stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"></path>
          <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"></path>
        </svg>
      </div>`,
    });
    const dcplLayer = L.layerGroup();
    dcplLayerRef.current = dcplLayer;

    fetch("/dcpl-branches.geojson")
      .then((r) => (r.ok ? r.json() : null))
      .then((geo) => {
        if (!geo || !dcplLayerRef.current) return;
        for (const f of geo.features || []) {
          const [lng, lat] = f.geometry?.coordinates || [];
          if (lat == null || lng == null) continue;
          const p = f.properties || {};
          const marker = L.marker([lat, lng], {
            icon: dcplIcon,
            alt: `${p.name} — DC Public Library branch in ${p.ward || "DC"}`,
            keyboard: true,
          });
          const branchUrl = p.web_url
            ? `<a href="${p.web_url}" target="_blank" rel="noopener noreferrer"
                style="color:#3DA9DC;text-decoration:none;font-weight:600;">
                Hours &amp; details ↗
              </a>`
            : "";
          const phoneLine = p.phone
            ? `<div style="font-size:12px;color:#1C1917;margin-top:2px;">
                 <strong>Phone:</strong> ${p.phone}
               </div>`
            : "";
          const typeLine = p.type && p.type !== "Neighborhood Library"
            ? `<div style="font-size:11px;color:#78716C;font-style:italic;margin-top:2px;">${p.type}</div>`
            : "";
          marker.bindTooltip(
            `<div style="font-family:Figtree,sans-serif;min-width:220px;max-width:260px;">
               <div style="font-family:Lora,serif;font-size:14px;font-weight:700;color:#1C1917;margin-bottom:4px;">${p.name}</div>
               ${typeLine}
               <div style="font-size:12px;color:#1C1917;margin-top:4px;">
                 <strong>Address:</strong> ${p.address || "—"}
               </div>
               <div style="font-size:12px;color:#1C1917;margin-top:2px;">
                 <strong>Ward:</strong> ${p.ward || "—"}
               </div>
               ${phoneLine}
               ${branchUrl ? `<div style="font-size:11px;margin-top:6px;">${branchUrl}</div>` : ""}
               <div style="font-size:10px;color:#A8A29E;margin-top:6px;font-style:italic;">
                 Source: DC Open Data — Library Locations
               </div>
             </div>`,
            { direction: "top", className: "bsp-ward-tooltip", sticky: false, offset: [0, -8] }
          );
          dcplLayer.addLayer(marker);
        }
      })
      .catch(() => { /* fail silently */ });

    const cluster = L.markerClusterGroup({
      showCoverageOnHover: false,
      maxClusterRadius: 60,
      disableClusteringAtZoom: 12,
      iconCreateFunction: (c) => {
        const count = c.getChildCount();
        const size = count < 10 ? "sm" : count < 50 ? "md" : "lg";
        return L.divIcon({
          className: "",
          html: `<div class="bsp-cluster bsp-cluster--${size}">${count}</div>`,
          iconSize: size === "sm" ? [40, 40] : size === "md" ? [52, 52] : [64, 64],
        });
      },
    });
    map.addLayer(cluster);
    const individual = L.layerGroup();
    map.addLayer(individual);

    mapRef.current = map;
    clusterRef.current = cluster;
    individualRef.current = individual;

    return () => {
      map.remove();
      mapRef.current = null;
      clusterRef.current = null;
      individualRef.current = null;
      choroplethRef.current = null;
      mdChoroplethRef.current = null;
      vaChoroplethRef.current = null;
      dcplLayerRef.current = null;
    };
  }, []);

  // Toggle the literacy choropleth layer visibility without removing it.
  useEffect(() => {
    if (!mapRef.current || !choroplethRef.current) return;
    const map = mapRef.current;
    if (showLiteracyLayer) {
      if (!map.hasLayer(choroplethRef.current)) map.addLayer(choroplethRef.current);
    } else if (map.hasLayer(choroplethRef.current)) {
      map.removeLayer(choroplethRef.current);
    }
  }, [showLiteracyLayer]);

  // Toggle the Maryland MCAP choropleth visibility.
  useEffect(() => {
    if (!mapRef.current || !mdChoroplethRef.current) return;
    const map = mapRef.current;
    if (showMdLiteracyLayer) {
      if (!map.hasLayer(mdChoroplethRef.current)) {
        map.addLayer(mdChoroplethRef.current);
        if (choroplethRef.current && choroplethRef.current.bringToFront) {
          try { choroplethRef.current.bringToFront(); } catch (_e) { /* noop */ }
        }
        if (clusterRef.current && clusterRef.current.bringToFront) clusterRef.current.bringToFront();
        if (individualRef.current && individualRef.current.bringToFront) individualRef.current.bringToFront();
      }
    } else if (map.hasLayer(mdChoroplethRef.current)) {
      map.removeLayer(mdChoroplethRef.current);
    }
  }, [showMdLiteracyLayer]);

  // Toggle the Virginia SOL Reading choropleth visibility.
  useEffect(() => {
    if (!mapRef.current || !vaChoroplethRef.current) return;
    const map = mapRef.current;
    if (showVaLiteracyLayer) {
      if (!map.hasLayer(vaChoroplethRef.current)) {
        map.addLayer(vaChoroplethRef.current);
        // Push DC, MD, and pin layers back to front so they stay on top of VA.
        if (mdChoroplethRef.current && mdChoroplethRef.current.bringToFront) {
          try { mdChoroplethRef.current.bringToFront(); } catch (_e) { /* noop */ }
        }
        if (choroplethRef.current && choroplethRef.current.bringToFront) {
          try { choroplethRef.current.bringToFront(); } catch (_e) { /* noop */ }
        }
        if (clusterRef.current && clusterRef.current.bringToFront) clusterRef.current.bringToFront();
        if (individualRef.current && individualRef.current.bringToFront) individualRef.current.bringToFront();
      }
    } else if (map.hasLayer(vaChoroplethRef.current)) {
      map.removeLayer(vaChoroplethRef.current);
    }
  }, [showVaLiteracyLayer]);

  // Toggle the DC Public Library branch markers.
  useEffect(() => {
    if (!mapRef.current || !dcplLayerRef.current) return;
    const map = mapRef.current;
    if (showDcplLayer) {
      if (!map.hasLayer(dcplLayerRef.current)) map.addLayer(dcplLayerRef.current);
    } else if (map.hasLayer(dcplLayerRef.current)) {
      map.removeLayer(dcplLayerRef.current);
    }
  }, [showDcplLayer]);

  // Auto-enable the VA layer once the user pans/zooms so the map center
  // sits clearly inside Virginia (i.e. west of -77.15 or south of 38.78 —
  // outside of DC's footprint). Only fires on user-initiated moves, not on
  // initial load, and only once (doesn't auto-disable when leaving).
  useEffect(() => {
    if (!mapRef.current || !setShowVaLiteracyLayer) return undefined;
    const map = mapRef.current;
    let userMoved = false;
    const onMoveStart = (e) => {
      // Only count human-driven moves (drag/zoom button). Programmatic moves
      // — e.g. our own fullscreen-fitBounds — have no originalEvent.
      if (e && e.originalEvent) userMoved = true;
    };
    const onMoveEnd = () => {
      if (showVaLiteracyLayer || !userMoved) return;
      const c = map.getCenter();
      const inVa =
        c.lat >= VA_BOUNDS.south && c.lat <= VA_BOUNDS.north &&
        c.lng >= VA_BOUNDS.west && c.lng <= VA_BOUNDS.east;
      const clearlyVa = inVa && (c.lng < -77.15 || c.lat < 38.78);
      if (clearlyVa) setShowVaLiteracyLayer(true);
    };
    map.on("movestart", onMoveStart);
    map.on("moveend", onMoveEnd);
    return () => {
      map.off("movestart", onMoveStart);
      map.off("moveend", onMoveEnd);
    };
  }, [showVaLiteracyLayer, setShowVaLiteracyLayer]);

  // Refresh markers when families/layers change
  useEffect(() => {
    if (!mapRef.current) return;
    const cluster = clusterRef.current;
    const individual = individualRef.current;
    cluster.clearLayers();
    individual.clearLayers();

    const filtered = families.filter((f) => layers[f.status]);
    filtered.forEach((f) => {
      const isNew = newlyInstalledIds.has(f.id);
      const marker = L.marker([f.lat, f.lng], { icon: makeIcon(f.status, isNew) });
      marker.bindPopup(popupHtml(f));
      marker.options.alt = `${f.status} library — ${f.neighborhood || f.jurisdiction}`;
      // Active markers also show outside cluster so the green pulse stays visible at all zooms.
      if (f.status === "active") {
        individual.addLayer(marker);
      } else {
        cluster.addLayer(marker);
      }
    });
  }, [families, layers, newlyInstalledIds]);

  // Delegated click handler so the popup's "Share this story" button
  // can ask the parent to open the ShareCardModal.
  useEffect(() => {
    if (!onShare) return undefined;
    const handler = (e) => {
      const btn = e.target.closest(".bsp-share-btn");
      if (!btn) return;
      const fid = btn.getAttribute("data-family-id");
      const family = families.find((x) => x.id === fid);
      if (!family) return;
      // Close the leaflet popup before the dialog opens so the popup
      // doesn't visually overlap the share-card modal.
      if (mapRef.current) mapRef.current.closePopup();
      onShare(family);
    };
    document.addEventListener("click", handler);
    return () => document.removeEventListener("click", handler);
  }, [families, onShare]);

  // When toggling fullscreen, the container size changes — tell Leaflet to recalc.
  useEffect(() => {
    if (!mapRef.current) return;
    const map = mapRef.current;
    // Run invalidateSize on the next frame so the new container size is
    // measurable, and then again after the layout settles.
    const r1 = window.requestAnimationFrame(() => {
      map.invalidateSize({ animate: false, pan: false });
      // Re-fit to a sensible view based on whether there are markers.
      try {
        const all = [];
        if (clusterRef.current) clusterRef.current.eachLayer((l) => all.push(l));
        if (individualRef.current) individualRef.current.eachLayer((l) => all.push(l));
        if (all.length > 0) {
          const group = L.featureGroup(all);
          map.fitBounds(group.getBounds().pad(0.25), { animate: false });
        } else {
          map.setView([38.9072, -77.0369], 10, { animate: false });
        }
      } catch (_e) {
        map.setView([38.9072, -77.0369], 10, { animate: false });
      }
    });
    const ids = [200, 500].map((ms) =>
      window.setTimeout(() => {
        if (mapRef.current) mapRef.current.invalidateSize({ animate: false, pan: false });
      }, ms)
    );
    return () => {
      window.cancelAnimationFrame(r1);
      ids.forEach((id) => window.clearTimeout(id));
    };
  }, [isFullscreen]);

  // Esc to exit fullscreen on desktop
  useEffect(() => {
    if (!isFullscreen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setIsFullscreen(false);
    };
    window.addEventListener("keydown", onKey);
    // Prevent body scroll behind the fullscreen map
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [isFullscreen]);

  return (
    <div
      className={
        isFullscreen
          ? "fixed inset-0 z-[9000] bg-white p-0"
          : "relative w-full h-[75vh] min-h-[440px] sm:h-[640px] sm:min-h-0 rounded-2xl overflow-hidden border border-stone-200 shadow-[0_8px_40px_rgba(28,25,23,0.08)]"
      }
    >
      <div
        ref={containerRef}
        data-testid={MAP.container}
        className="w-full h-full"
        role="region"
        aria-label="Interactive map of home library installations across the DMV"
      />
      <button
        type="button"
        onClick={() => setIsFullscreen((v) => !v)}
        aria-label={isFullscreen ? "Exit fullscreen map" : "View map fullscreen"}
        data-testid="map-fullscreen-toggle"
        className="absolute top-3 right-3 z-[1000] grid place-items-center w-11 h-11 rounded-full bg-white/95 backdrop-blur border border-stone-200 shadow-[0_4px_16px_rgba(28,25,23,0.18)] text-[#122036] hover:bg-white hover:scale-105 active:scale-95 transition"
      >
        {isFullscreen ? <Minimize2 size={18} /> : <Maximize2 size={18} />}
      </button>
      {isFullscreen && (
        <div className="absolute top-3 left-1/2 -translate-x-1/2 z-[1000] px-3 py-1.5 rounded-full bg-white/95 backdrop-blur border border-stone-200 shadow-sm text-xs text-stone-600 pointer-events-none">
          Tap the icon to exit · pinch to zoom
        </div>
      )}
      {(showLiteracyLayer || showMdLiteracyLayer || showVaLiteracyLayer || showDcplLayer) && (
        <div
          data-testid="literacy-legend"
          aria-label={
            showVaLiteracyLayer
              ? VA_SOL_SR_DESCRIPTION
              : showMdLiteracyLayer
              ? MD_ELA_SR_DESCRIPTION
              : WARD_ELA_SR_DESCRIPTION
          }
          className="absolute bottom-4 left-4 z-[1000] w-[230px] sm:w-[256px] max-h-[78vh] overflow-y-auto rounded-xl bg-white/95 backdrop-blur border border-stone-200 shadow-[0_6px_24px_rgba(28,25,23,0.12)] p-3 pointer-events-auto"
        >
          {showLiteracyLayer && (
            <>
              <div className="font-serif text-[13px] font-semibold text-[#122036] leading-tight">
                DC Wards — ELA Proficiency
              </div>
              <div className="text-[10px] uppercase tracking-[0.14em] text-stone-500 font-semibold mt-0.5">
                2025 DC CAPE
              </div>
              <div className="mt-2 h-2.5 w-full rounded-full" style={{
                background: "linear-gradient(to right, #FFEBEE 0%, #FFCDD2 18%, #EF9A9A 38%, #E57373 58%, #C62828 80%, #B71C1C 100%)",
              }} />
              <div className="mt-1 flex items-center justify-between text-[10px] text-stone-600">
                <span>Higher</span>
                <span>Lower</span>
              </div>
              <div className="mt-2 text-[10px] text-stone-500 leading-snug">
                Source: {WARD_ELA_SOURCE}
              </div>
            </>
          )}
          {showLiteracyLayer && (showMdLiteracyLayer || showVaLiteracyLayer) && (
            <div className="my-3 border-t border-stone-200" aria-hidden />
          )}
          {showMdLiteracyLayer && (
            <>
              <div className="font-serif text-[13px] font-semibold text-[#122036] leading-tight">
                Maryland Counties — ELA Proficiency
              </div>
              <div className="text-[10px] uppercase tracking-[0.14em] text-stone-500 font-semibold mt-0.5">
                MCAP 2024–25
              </div>
              <div className="mt-2 h-2.5 w-full rounded-full" style={{
                background: "linear-gradient(to right, #FFEDED 0%, #FFBDBD 25%, #FF8A8A 50%, #FF5252 75%, #C62828 100%)",
              }} />
              <div className="mt-1 flex items-center justify-between text-[10px] text-stone-600">
                <span>≥65%</span>
                <span>&lt;35%</span>
              </div>
              <div className="mt-2 text-[10px] text-stone-500 leading-snug">
                Statewide avg: {MD_STATEWIDE_AVG_PCT}% · Source: {MD_ELA_SOURCE}
              </div>
            </>
          )}
          {showMdLiteracyLayer && showVaLiteracyLayer && (
            <div className="my-3 border-t border-stone-200" aria-hidden />
          )}
          {showVaLiteracyLayer && (
            <>
              <div className="flex items-start justify-between gap-2">
                <div className="font-serif text-[13px] font-semibold text-[#122036] leading-tight">
                  Virginia Divisions — SOL Reading
                </div>
                {VA_NEW_RIGOR_STANDARD && (
                  <span
                    data-testid="va-rigor-badge"
                    title={VA_RIGOR_CHANGE_NOTE}
                    className="shrink-0 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-amber-100 border border-amber-300 text-amber-900 text-[9px] font-bold uppercase tracking-wider"
                  >
                    <span aria-hidden className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                    {VA_RIGOR_BADGE_LABEL}
                  </span>
                )}
              </div>
              <div className="text-[10px] uppercase tracking-[0.14em] text-stone-500 font-semibold mt-0.5">
                VDOE {VA_DATA_SCHOOL_YEAR} · Pass Rate
              </div>
              <div className="mt-2 h-2.5 w-full rounded-full" style={{
                background: "linear-gradient(to right, #FFEDED 0%, #FFBDBD 25%, #FF8A8A 50%, #FF5252 75%, #C62828 100%)",
              }} />
              <div className="mt-1 flex items-center justify-between text-[10px] text-stone-600">
                <span>≥85%</span>
                <span>&lt;55%</span>
              </div>
              <div className="mt-2 text-[10px] text-stone-500 leading-snug">
                Statewide avg: {VA_STATEWIDE_PCT}% · Source: {VA_SOL_SOURCE}
              </div>
              {VA_NEW_RIGOR_STANDARD && (
                <div
                  data-testid="va-rigor-warning"
                  className="mt-2 text-[10px] text-amber-900 leading-snug bg-amber-50 border border-amber-200 rounded-md p-2"
                >
                  <strong>{VA_RIGOR_BADGE_LABEL}.</strong> {VA_RIGOR_CHANGE_NOTE}
                </div>
              )}
              <button
                type="button"
                onClick={() => setShowVaRigor((v) => !v)}
                aria-label="About 2024–25 SOL rigor"
                data-testid="va-rigor-info-btn"
                className="mt-1.5 flex items-center gap-1 text-[10px] text-[#3DA9DC] hover:text-[#2C8FBE] font-semibold"
              >
                <span aria-hidden className="inline-grid place-items-center w-3.5 h-3.5 rounded-full border border-current text-[9px] leading-none">i</span>
                Why {VA_DATA_SCHOOL_YEAR} looks lower
              </button>
              {showVaRigor && (
                <div className="mt-1.5 text-[10px] text-stone-600 leading-snug bg-stone-50 border border-stone-200 rounded-md p-2">
                  {VA_SOL_RIGOR_NOTE}
                </div>
              )}
            </>
          )}
          {(showLiteracyLayer || showMdLiteracyLayer || showVaLiteracyLayer) && showDcplLayer && (
            <div className="my-3 border-t border-stone-200" aria-hidden />
          )}
          {showDcplLayer && (
            <>
              <div className="flex items-center gap-2">
                <span aria-hidden className="inline-grid place-items-center w-4 h-4 rounded-full" style={{ background: "#B89968" }}>
                  <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
                    <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
                  </svg>
                </span>
                <div className="font-serif text-[13px] font-semibold text-[#122036] leading-tight">
                  DC Public Library Branches
                </div>
              </div>
              <div className="text-[10px] uppercase tracking-[0.14em] text-stone-500 font-semibold mt-0.5 pl-6">
                DCPL · 28 open branches
              </div>
              <div className="mt-2 text-[11px] italic text-stone-600 leading-snug pl-6">
                In-home libraries extend literacy access beyond institutional reach.
              </div>
              <div className="mt-2 text-[10px] text-stone-500 leading-snug pl-6">
                Source: DC Open Data
              </div>
            </>
          )}
        </div>
      )}
      {showLiteracyLayer && showMdLiteracyLayer && !insightDismissed && (
        <div
          data-testid="cross-border-insight"
          className="absolute bottom-4 right-4 z-[1000] w-[268px] sm:w-[296px] rounded-xl bg-[#FAF6EE] border border-[#E7E0CF] shadow-[0_8px_28px_rgba(28,25,23,0.14)] p-4"
        >
          <button
            type="button"
            onClick={() => setInsightDismissed(true)}
            aria-label="Dismiss insight card"
            data-testid="cross-border-insight-dismiss"
            className="absolute top-2 right-2 grid place-items-center w-6 h-6 rounded-full text-stone-500 hover:bg-stone-200/70 transition"
          >
            <span aria-hidden className="text-base leading-none">×</span>
          </button>
          <div className="font-serif text-[14px] font-semibold text-[#122036] leading-tight pr-6">
            {showVaLiteracyLayer ? "DMV Literacy Snapshot · SY 2024–25" : "Literacy Need in Our Region"}
          </div>
          <ul className="mt-2.5 space-y-1.5 text-[12px] text-stone-700">
            <li>
              <span className="font-semibold text-[#122036]">DC Wards 7 &amp; 8:</span> 44% / 43% ELA
              <span className="text-stone-500"> (DC CAPE 2025)</span>
            </li>
            <li>
              <span className="font-semibold text-[#122036]">Prince George&apos;s County:</span> 38.3%
              <span className="text-stone-500"> (MD MCAP 2024–25)</span>
            </li>
            <li>
              <span className="font-semibold text-[#122036]">Baltimore City:</span> 31.2%
              <span className="text-stone-500"> (MD MCAP 2024–25)</span>
            </li>
            {showVaLiteracyLayer && (
              <>
                <li className="pt-1.5 mt-1.5 border-t border-[#E7E0CF]">
                  <span className="font-semibold text-[#122036]">Fairfax County:</span> 79%
                  <span className="text-stone-500"> (VA SOL 2024–25)</span>
                </li>
                <li>
                  <span className="font-semibold text-[#122036]">Alexandria City:</span> 72%
                  <span className="text-stone-500"> (VA SOL 2024–25)</span>
                </li>
                <li>
                  <span className="font-semibold text-[#122036]">Prince William County:</span> 76%
                  <span className="text-stone-500"> (VA SOL 2024–25)</span>
                </li>
              </>
            )}
          </ul>
          <div className="mt-3 pt-2.5 border-t border-[#E7E0CF] text-[11px] text-stone-600 leading-snug">
            The Bookshelf Project serves families across this region.
          </div>
        </div>
      )}
      <span className="sr-only">{WARD_ELA_SR_DESCRIPTION}</span>
      {showMdLiteracyLayer && <span className="sr-only">{MD_ELA_SR_DESCRIPTION}</span>}
      {showVaLiteracyLayer && <span className="sr-only">{VA_SOL_SR_DESCRIPTION}</span>}
    </div>
  );
}
